import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Wallet, Clock, 
  RefreshCw, PieChart, ArrowDownLeft, ArrowUpRight, DollarSign, Activity, Plus, Shield
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getInvestments, getTransactions, getPlatformSettings } from '../services/api';
import { toast } from 'sonner';

const PortfolioPage = () => {
  const { user, refreshUser } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [investRes, txRes, settingsRes] = await Promise.all([
        getInvestments(),
        getTransactions(20),
        getPlatformSettings()
      ]);
      setInvestments(investRes.data);
      setTransactions(txRes.data);
      setSettings(settingsRes.data);
      await refreshUser();
    } catch (error) {
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const activeInvestment = investments.find(i => i.status === 'active');
  const closedInvestments = investments.filter(i => i.status === 'closed');

  const tradingBalance = activeInvestment?.current_value || 0;
  const originalDeposit = activeInvestment?.amount || 0;
  const totalProfit = activeInvestment?.profit_loss || 0;
  const dailyProfitRate = parseFloat(settings.daily_profit_percentage || 3);

  const chartData = (() => {
    if (!activeInvestment) return [];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map((day, index) => ({
      name: day,
      value: originalDeposit * (1 + (dailyProfitRate / 100) * index)
    }));
  })();

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-emerald-400" />;
      case 'withdrawal': return <ArrowUpRight className="w-4 h-4 text-orange-400" />;
      case 'investment': return <TrendingUp className="w-4 h-4 text-cyan-400" />;
      case 'investment_withdrawal': return <ArrowUpRight className="w-4 h-4 text-purple-400" />;
      case 'profit':
      case 'daily_profit': return <DollarSign className="w-4 h-4 text-emerald-400" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="animated-bg" />
      <div className="noise-overlay" />
      <Navbar />

      <main className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6 overflow-x-hidden" data-testid="portfolio-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
                Trading Portfolio
              </h1>
              <div className="flex items-center gap-3 mt-2">
                {activeInvestment && (
                  <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Automated Trading Active
                  </span>
                )}
              </div>
            </div>
            <button onClick={fetchData} className="btn-secondary flex items-center gap-2 !py-2.5 !px-5 !text-sm" data-testid="refresh-portfolio">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Automated Trading Info Banner */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="flex items-start gap-3 px-5 py-4 rounded-xl bg-cyan-500/5 border border-cyan-500/10 mb-6" data-testid="auto-trading-info"
              >
                <Activity className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Fully Automated Trading</p>
                  <p className="text-xs text-slate-400 mt-1">All confirmed deposits are automatically activated for trading at {dailyProfitRate}% daily. No manual action required. Withdraw anytime from your balance.</p>
                </div>
              </motion.div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="stat-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Trading Balance</span>
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center">
                      <Wallet className="w-4.5 h-4.5 text-cyan-400" />
                    </div>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="trading-balance">{formatCurrency(tradingBalance)}</p>
                  <p className="text-xs mt-2 text-slate-500">Actively generating returns</p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="stat-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Total Deposited</span>
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center">
                      <ArrowDownLeft className="w-4.5 h-4.5 text-purple-400" />
                    </div>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="total-deposited">{formatCurrency(originalDeposit)}</p>
                  <p className="text-xs mt-2 text-slate-500">Original deposits</p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="stat-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Total Profit</span>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${totalProfit >= 0 ? 'bg-gradient-to-br from-emerald-500/10 to-green-500/10' : 'bg-gradient-to-br from-red-500/10 to-orange-500/10'}`}>
                      {totalProfit >= 0 ? <TrendingUp className="w-4.5 h-4.5 text-emerald-400" /> : <TrendingDown className="w-4.5 h-4.5 text-red-400" />}
                    </div>
                  </div>
                  <p className={`text-2xl sm:text-3xl font-bold ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="total-profit">
                    {totalProfit >= 0 ? '+' : ''}{formatCurrency(totalProfit)}
                  </p>
                  <p className="text-xs mt-2 text-slate-500">Lifetime earnings</p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="stat-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Daily Rate</span>
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/10 to-yellow-500/10 flex items-center justify-center">
                      <PieChart className="w-4.5 h-4.5 text-orange-400" />
                    </div>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-400" data-testid="daily-rate">{dailyProfitRate}%</p>
                  <p className="text-xs mt-2 text-emerald-400/70">Est. daily: +{formatCurrency(tradingBalance * dailyProfitRate / 100)}</p>
                </motion.div>
              </div>

              <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">
                {/* Trading Performance Chart */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-7"
                >
                  <h2 className="text-lg font-bold text-white mb-5">Trading Performance</h2>

                  {activeInvestment ? (
                    <>
                      <div className="h-56 sm:h-64" data-testid="portfolio-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData}>
                            <defs>
                              <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.25} />
                                <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 11 }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 11 }} tickFormatter={(v) => `$${v.toLocaleString()}`} width={60} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#0a0f1a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#fff', fontSize: '13px', padding: '8px 12px' }}
                              formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Value']}
                            />
                            <Area type="monotone" dataKey="value" stroke="#00F0FF" strokeWidth={2.5} fillOpacity={1} fill="url(#portfolioGrad)" animationDuration={1500} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Trading Details */}
                      <div className="mt-5 p-4 sm:p-5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <h3 className="text-white font-semibold text-sm">Active Trading Session</h3>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Started</p>
                            <p className="text-sm font-semibold text-white">{formatDate(activeInvestment.start_date)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Original Deposit</p>
                            <p className="text-sm font-semibold text-white">{formatCurrency(activeInvestment.amount)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Current Value</p>
                            <p className="text-sm font-semibold text-white">{formatCurrency(activeInvestment.current_value)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Profit/Loss</p>
                            <p className={`text-sm font-semibold ${activeInvestment.profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {activeInvestment.profit_loss >= 0 ? '+' : ''}{formatCurrency(activeInvestment.profit_loss)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Activity className="w-14 h-14 mx-auto mb-4 text-slate-700" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Active Trading</h3>
                      <p className="text-sm text-slate-400 mb-6">Make a deposit to start automated trading</p>
                      <Link to="/deposit" className="btn-primary inline-flex items-center gap-2" data-testid="start-deposit-btn">
                        <Plus className="w-4 h-4" /> Make a Deposit
                      </Link>
                    </div>
                  )}
                </motion.div>

                {/* Recent Transactions */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-6"
                >
                  <h2 className="text-lg font-bold text-white mb-5">Recent Transactions</h2>
                  <div className="space-y-2.5 max-h-[500px] overflow-y-auto" data-testid="transaction-list">
                    {transactions.length > 0 ? (
                      transactions.slice(0, 10).map((tx, i) => (
                        <motion.div key={tx.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 + i * 0.04 }}
                          className="flex items-center gap-3 py-3 px-3 rounded-xl border border-white/[0.03] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                            {getTransactionIcon(tx.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{tx.description}</p>
                            <p className="text-slate-600 text-xs">{formatDate(tx.created_at)}</p>
                          </div>
                          <p className={`text-sm font-semibold whitespace-nowrap ${tx.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </p>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-10">
                        <Clock className="w-10 h-10 mx-auto mb-3 text-slate-700" />
                        <p className="text-sm text-slate-500">No transactions yet</p>
                        <Link to="/deposit" className="text-cyan-400 text-xs mt-2 inline-block hover:text-cyan-300">Make your first deposit</Link>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Quick Actions */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="mt-6 grid grid-cols-2 gap-4"
              >
                <Link to="/deposit" className="flex items-center gap-3 px-5 py-4 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.08] transition-all group" data-testid="portfolio-deposit-btn">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <ArrowDownLeft className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">Deposit More</span>
                    <p className="text-xs text-slate-500">Add funds to trading</p>
                  </div>
                </Link>
                <Link to="/withdraw" className="flex items-center gap-3 px-5 py-4 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.08] transition-all group" data-testid="portfolio-withdraw-btn">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">Withdraw Funds</span>
                    <p className="text-xs text-slate-500">Cash out your profits</p>
                  </div>
                </Link>
              </motion.div>

              {/* Closed Trading History */}
              {closedInvestments.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                  className="mt-6 rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-6"
                >
                  <h2 className="text-lg font-bold text-white mb-5">Trading History</h2>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Amount</th>
                          <th>Final Value</th>
                          <th>Profit/Loss</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {closedInvestments.map((inv) => (
                          <tr key={inv.id}>
                            <td className="text-slate-400">{formatDate(inv.start_date)}</td>
                            <td className="text-slate-400">{formatDate(inv.end_date || new Date())}</td>
                            <td className="text-white">{formatCurrency(inv.amount)}</td>
                            <td className="text-white">{formatCurrency(inv.current_value)}</td>
                            <td className={inv.profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {inv.profit_loss >= 0 ? '+' : ''}{formatCurrency(inv.profit_loss)}
                            </td>
                            <td><span className="badge badge-info">Closed</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PortfolioPage;
